import React, { useState, useEffect, useRef } from 'react';
import { User, Building2, MapPin, Hash, CheckCircle2, ChevronDown } from 'lucide-react';
import { INDIAN_STATES } from '../utils/constants';

const PartyDetails = ({ title, details, onChange, icon, isPreview, isBillOfSupply = false, compact = false, allowNoGst = true, hideAddress = false }) => {
    const [noGst, setNoGst] = useState(false);
    const [showStateDropdown, setShowStateDropdown] = useState(false);
    const [stateSearch, setStateSearch] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        // If external details have a GST number, uncheck the box (unless it's NOT APPLICABLE)
        if (details.gstNumber && details.gstNumber.trim() !== '' && details.gstNumber !== 'NOT APPLICABLE') {
            setNoGst(false);
        } else if (details.gstNumber === 'NOT APPLICABLE') {
            setNoGst(true);
        }
    }, [details.gstNumber]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowStateDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (field, value) => {
        onChange({ ...details, [field]: value });
    };

    const handleNoGstToggle = (e) => {
        const isChecked = e.target.checked;
        setNoGst(isChecked);
        if (isChecked) {
            handleChange('gstNumber', 'NOT APPLICABLE');
        } else {
            handleChange('gstNumber', '');
        }
    };

    const handleStateSelect = (state) => {
        handleChange('state', state);
        setShowStateDropdown(false);
        setStateSearch('');
    };

    const filteredStates = INDIAN_STATES.filter(state =>
        state.toLowerCase().includes(stateSearch.toLowerCase())
    );

    const Icon = icon === 'store' ? Building2 : User;

    return (
        <div className={`group`}>
            {!compact && (
                <div className={`flex items-center gap-2 mb-4 p-2 ${isPreview ? '' : 'border-b border-slate-100'}`}>
                    {!isPreview && <Icon size={18} className="text-blue-900" />}
                    <h2 className={`text-sm font-bold uppercase tracking-wide ${isPreview ? 'text-slate-500 mb-1' : 'text-slate-700'}`}>{title}</h2>
                </div>
            )}

            <div className="space-y-3">
                {!isPreview && <label className="block text-sm font-semibold text-slate-700 mb-2">Name / Business <span className="text-red-500">*</span></label>}
                {isPreview ? (
                    <div className="text-slate-900 font-bold text-lg">{details.name || 'Business Name'}</div>
                ) : (
                    <input
                        type="text"
                        value={details.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-600 outline-none transition-all placeholder:text-slate-400 text-slate-800 text-sm font-medium"
                        placeholder="Enter name"
                    />
                )}
            </div>

            {!isBillOfSupply && (
                <div>
                    {!isPreview && (
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-semibold text-slate-700">
                                GSTIN {!noGst && <span className="text-red-500">*</span>}
                            </label>
                            {allowNoGst && (
                                <label className="flex items-center gap-2 cursor-pointer group/check">
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            checked={noGst}
                                            onChange={handleNoGstToggle}
                                            className="peer sr-only"
                                        />
                                        <div className="w-4 h-4 border-2 border-slate-300 rounded bg-white peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-colors"></div>
                                        <CheckCircle2 size={12} className="absolute top-0.5 left-0.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity" />
                                    </div>
                                    <span className="text-xs font-medium text-slate-500 group-hover/check:text-blue-600 transition-colors select-none">
                                        Not Applicable / No GSTIN
                                    </span>
                                </label>
                            )}
                        </div>
                    )}

                    {isPreview ? (
                        details.gstNumber && <div className="text-slate-600 text-sm font-medium mt-1">GSTIN: {details.gstNumber}</div>
                    ) : (
                        <div className="relative">
                            <input
                                type="text"
                                value={details.gstNumber}
                                onChange={(e) => handleChange('gstNumber', e.target.value)}
                                disabled={noGst}
                                className={`w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg outline-none transition-all text-slate-800 text-sm font-medium uppercase
                                    ${noGst
                                        ? 'opacity-50 cursor-not-allowed bg-slate-100 placeholder:text-slate-400'
                                        : 'focus:ring-2 focus:ring-blue-100 focus:border-blue-600 placeholder:text-slate-400'
                                    }
                                `}
                                placeholder={noGst ? "Not Applicable" : "Ex. 29ABCDE1234F1Z5"}
                            />
                        </div>
                    )}
                </div>
            )}

            <div>
                {!isPreview && <label className="block text-sm font-semibold text-slate-700 mb-2">State <span className="text-red-500">*</span></label>}
                {isPreview ? (
                    details.state && <div className="text-slate-600 text-sm font-medium mt-1">State: {details.state}</div>
                ) : (
                    <div className="relative" ref={dropdownRef}>
                        <div className="relative">
                            <input
                                type="text"
                                value={showStateDropdown ? stateSearch : details.state || ''}
                                onChange={(e) => {
                                    setStateSearch(e.target.value);
                                    if (!showStateDropdown) setShowStateDropdown(true);
                                }}
                                onFocus={() => setShowStateDropdown(true)}
                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-600 outline-none transition-all placeholder:text-slate-400 text-slate-800 text-sm font-medium pr-10"
                                placeholder="Search and select state"
                            />
                            <ChevronDown
                                size={18}
                                className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-transform ${showStateDropdown ? 'rotate-180' : ''}`}
                            />
                        </div>
                        {showStateDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {filteredStates.length > 0 ? (
                                    filteredStates.map((state) => (
                                        <div
                                            key={state}
                                            onClick={() => handleStateSelect(state)}
                                            className="px-4 py-2.5 hover:bg-blue-50 cursor-pointer text-sm text-slate-700 transition-colors"
                                        >
                                            {state}
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-4 py-2.5 text-sm text-slate-400">No states found</div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!hideAddress && (
                <div>
                    {!isPreview && <label className="block text-sm font-semibold text-slate-700 mb-2">Address <span className="text-red-500">*</span></label>}
                    {isPreview ? (
                        <div className="text-slate-500 text-sm mt-1 whitespace-pre-wrap leading-relaxed">{details.address}</div>
                    ) : (
                        <textarea
                            value={details.address}
                            onChange={(e) => handleChange('address', e.target.value)}
                            rows="3"
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-600 outline-none transition-all placeholder:text-slate-400 text-slate-800 text-sm font-medium resize-none"
                            placeholder="Enter complete address details..."
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default PartyDetails;
